/* eslint-disable react-hooks/exhaustive-deps */
import Box from '@amzn/awsui-components-react/polaris/box';
import Pagination from '@amzn/awsui-components-react/polaris/pagination';
import Table from '@amzn/awsui-components-react/polaris/table';
import * as queryString from 'query-string';
import React, {useEffect, useState, useRef} from 'react';
import {RouteComponentProps} from 'react-router-dom';
import {fetchWorkflowExecutionHistory, fetchWorkflowExecutionsHistory} from '../../backend/backend';
import {getWorkflowHistoryPageUrl} from '../../backend/internalUrls';
import {
    InputQuery, OutputQuery,
    WorkflowExecutionDetails,
    WorkflowExecutionHistory,
    WorkflowExecutionStatus
} from '../../backend/model';
import {parseErrorMessage} from '../../utils/errors';
import {isValidExecutionId} from '../../validation/InputValidator';
import AjaxStatusIndicator from '../basic/AjaxStatusIndicator';
import {emptyTableDiv} from '../TableTemplate';
import {parseDate, prettifyDateUTC} from './date';
import './workflow.css';
import WorkflowHistorySummaryPanel from './WorkflowHistoryDetailsPanel';
import WorkflowHistorySearchPanel from './WorkflowHistorySearchPanel';
import {workflowHistoryColumns} from './WorkflowHistoryTableMetadata';
import {getBlobUrl, getHeaders, tableHeader} from "../TableHeaderTemplate";
import {
    constructAdvancedSearchQuery,
    SPECIAL_PURPOSED_QUERY_PROPERTY,
} from '../search/SearchUtils';
import {
    INPUT_FIELD_PREFIX,
    OUTPUT_FIELD_PREFIX,
} from '../metadata/Constants';

const PAGE_SIZE = 20
const PAGE_SIZE_ALL = 10000

type WorkflowHistoryPageProps = RouteComponentProps<{ executionId?: string }>

const WorkflowHistoryPage: React.FC<WorkflowHistoryPageProps> = (props) => {
    const [workflowExecution, setWorkflowExecution] = useState<WorkflowExecutionDetails>()
    const [workflowExecutionHistory, setWorkflowExecutionHistory] = useState<WorkflowExecutionHistory>()
    const [allWorkflowExecutionHistory, setAllWorkflowExecutionHistory] = useState<string[][]>([])
    const [downloadClicked, setDownloadClicked] = useState<boolean>(false)
    const [viewAllClicked, setViewAllClicked] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessages, setErrorMessages] = useState<string[]>([])

    const executionIdFromUrl = (props.match.params.executionId as string)?.trim() || ''
    const queries = queryString.parse(props.location.search) || ''
    const pageFromUrl = (queries.page as string)?.trim() || ''
    const workflowNameFromUrl = (queries.workflowName as string)?.trim() || ''
    const statusFromUrl = queries.status as WorkflowExecutionStatus
    const requesterFromUrl = (queries.requester as string)?.trim() || ''
    const fromStartDateFromUrl = (queries.fromStartDate as string)?.trim() || ''
    const toStartDateFromUrl = (queries.toStartDate as string)?.trim() || ''
    const searchViewFromUrl = (queries.view as string)?.trim()

    const numberOfPages = workflowExecutionHistory ? Math.ceil(workflowExecutionHistory.count / PAGE_SIZE) : 0

    const csvInstance = useRef<any>(null)

    const queryFilterHandlerCreator = (prefix: string) => {
        return (key: string) => {
            const prefixExp = new RegExp(`^${prefix}.*`)
            return !SPECIAL_PURPOSED_QUERY_PROPERTY.includes(key) && prefixExp.test(key)
        }
    }
    const inputSearchQuery = constructAdvancedSearchQuery(queries, queryFilterHandlerCreator(INPUT_FIELD_PREFIX), INPUT_FIELD_PREFIX)
    const outputSearchQuery = constructAdvancedSearchQuery(queries, queryFilterHandlerCreator(OUTPUT_FIELD_PREFIX), OUTPUT_FIELD_PREFIX)

    const resetState = (): void => {
        setWorkflowExecution(undefined)
        setWorkflowExecutionHistory(undefined)
        setAllWorkflowExecutionHistory([])
        setErrorMessages([])
    }

    useEffect(() => {
        if (allWorkflowExecutionHistory.length > 0) {
            if (downloadClicked) {
                if (csvInstance && csvInstance.current) {
                    setTimeout(() => {
                        csvInstance.current.click()
                        setDownloadClicked(false)
                    })
                }
            }

            if (viewAllClicked) {
                window.open(getBlobUrl(allWorkflowExecutionHistory, 'text/plain'))
                setViewAllClicked(false)
            }
        }
    }, [allWorkflowExecutionHistory, downloadClicked, viewAllClicked])

    const getHeaderDetails = () => {
        return {
            header: `${workflowExecutionHistory?.count} Executions`,
            fileName: 'Executions',
            data: allWorkflowExecutionHistory,
            csvInstance: csvInstance,
            downloadButtonDetails: {
                onClickDownload: onClickDownload,
                tooltip: `Download (max ${PAGE_SIZE_ALL} elements)`,
                tooltipPlacement: 'left'
            },
            onClickViewAll: onClickViewAll,
        }
    }

    const tableContent = (
        <Table
            id='workflowExecutionHistoryTable'
            header={ tableHeader(getHeaderDetails()) }
            stickyHeader
            columnDefinitions={workflowHistoryColumns}
            items={workflowExecutionHistory?.executions ?? []}
            wrapLines
            empty={emptyTableDiv}
            pagination={
                <Pagination
                    currentPageIndex={parseInt(pageFromUrl)}
                    pagesCount={numberOfPages}
                    openEnd
                    onChange={({detail}) => {
                        const newPageNumber = detail.currentPageIndex > numberOfPages ? 1 : detail.currentPageIndex
                        props.history.push(
                            getWorkflowHistoryPageUrl(
                                executionIdFromUrl,
                                newPageNumber,
                                workflowNameFromUrl,
                                statusFromUrl,
                                requesterFromUrl,
                                fromStartDateFromUrl,
                                toStartDateFromUrl,
                                inputSearchQuery,
                                outputSearchQuery,
                                searchViewFromUrl,
                            ),
                        )
                    }}
                />
            }
        />
    )

    useEffect(() => {
        getExecutionHistory(
            executionIdFromUrl,
            parseInt(pageFromUrl),
            workflowNameFromUrl,
            statusFromUrl,
            requesterFromUrl,
            fromStartDateFromUrl,
            toStartDateFromUrl,
            inputSearchQuery,
            outputSearchQuery,
        )
    }, [
        executionIdFromUrl,
        pageFromUrl,
        workflowNameFromUrl,
        statusFromUrl,
        requesterFromUrl,
        fromStartDateFromUrl,
        toStartDateFromUrl,
        history.length
    ])

    const getExecutionHistory = async (
        executionId: string,
        page: number,
        workflowName: string,
        status: WorkflowExecutionStatus,
        requester: string,
        fromStartDate: string,
        toStartDate: string,
        inputQuery?: InputQuery,
        outputQuery?: OutputQuery,
    ) => {
        resetState()

        if (isValidExecutionId(executionId)) {
            await fetchWorkflowExecutionByExecutionIdTask(executionId)
        } else if (!!page && !executionId) {
            await fetchWorkflowExecutionHistoryTask(
                page,
                workflowName,
                status,
                requester,
                fromStartDate,
                toStartDate,
                inputQuery,
                outputQuery,
            )
        }
    }

    async function fetchWorkflowExecutionByExecutionIdTask(executionId: string) {
        setIsLoading(true)

        try {
            const workflowExecution = (await fetchWorkflowExecutionHistory(executionId)).data
            setWorkflowExecution(workflowExecution)
        } catch (error: any) {
            console.log(error)
            setErrorMessages([parseErrorMessage(error)])
            setWorkflowExecution(undefined)
        } finally {
            setIsLoading(false)
        }
    }

    async function fetchWorkflowExecutionHistoryTask(
        page: number,
        workflowName: string,
        status: WorkflowExecutionStatus,
        requester: string,
        fromStartDate: string,
        toStartDate: string,
        inputQuery?: InputQuery,
        outputQuery?: OutputQuery,
    ) {
        setIsLoading(true)

        try {
            const workflowExecutionHistory = (
                await fetchWorkflowExecutionsHistory(
                    PAGE_SIZE,
                    page - 1,
                    workflowName,
                    status,
                    requester,
                    !!fromStartDate ? parseDate(fromStartDate, true) : undefined,
                    !!toStartDate ? parseDate(toStartDate, false) : undefined,
                    inputQuery,
                    outputQuery,
                )).data
            setWorkflowExecutionHistory(workflowExecutionHistory)
        } catch (error: any) {
            console.log(error)
            setErrorMessages([parseErrorMessage(error)])
            setWorkflowExecutionHistory(undefined)
        } finally {
            setIsLoading(false)
        }
    }

    async function onClickDownload() {
        if (allWorkflowExecutionHistory.length === 0) {
            await fetchAllExecutionHistoryTask()
        }

        setDownloadClicked(true)
    }

    async function onClickViewAll() {
        if (allWorkflowExecutionHistory.length === 0) {
            await fetchAllExecutionHistoryTask()
        }

        setViewAllClicked(true)
    }

    async function fetchAllExecutionHistoryTask() {
        setIsLoading(true)

        try {
            const headers = getHeaders(workflowHistoryColumns)

            const executions = (await fetchWorkflowExecutionsHistory(
                PAGE_SIZE_ALL,
                0,
                workflowNameFromUrl,
                statusFromUrl,
                requesterFromUrl,
                !!fromStartDateFromUrl ? parseDate(fromStartDateFromUrl, true) : undefined,
                !!toStartDateFromUrl ? parseDate(toStartDateFromUrl, false) : undefined,
            )).data.executions.map((item) => {
                return [
                    item.executionId,
                    item.workflowName,
                    item.status,
                    item.region ?? '',
                    item.startedBy,
                    prettifyDateUTC(item.startedAt),
                    prettifyDateUTC(item.lastModifiedAt),
                ]
            })

            setAllWorkflowExecutionHistory(headers.concat(executions))
        } catch (error: any) {
            console.log(error)
            setErrorMessages([parseErrorMessage(error)])
            setAllWorkflowExecutionHistory([])
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div id='workflow-history'>
            <Box variant="h1">Workflow Executions</Box>
            <WorkflowHistorySearchPanel
                executionId={executionIdFromUrl}
                workflowName={workflowNameFromUrl}
                status={statusFromUrl}
                requester={requesterFromUrl}
                fromStartDate={fromStartDateFromUrl}
                toStartDate={toStartDateFromUrl}
                inputSearchQuery={inputSearchQuery}
                outputSearchQuery={outputSearchQuery}
                searchView={searchViewFromUrl}
                searchHandler={
                    (
                        executionId: string,
                        workflowName: string,
                        status: WorkflowExecutionStatus,
                        requester: string,
                        fromStartDate: string,
                        toStartDate: string,
                        inputSearchQuery: InputQuery,
                        outputSearchQuery: OutputQuery,
                        searchView: string
                    ) => {
                        props.history.push(
                            getWorkflowHistoryPageUrl(
                                executionId,
                                1,
                                workflowName,
                                status,
                                requester,
                                fromStartDate,
                                toStartDate,
                                inputSearchQuery,
                                outputSearchQuery,
                                searchView,
                            ),
                        )
                    }
                }
            />
            <br/>
            <AjaxStatusIndicator errorMessage={errorMessages.join(' ')} loading={isLoading}/>
            <br/>
            {
                workflowExecution &&
                <WorkflowHistorySummaryPanel workflowExecution={workflowExecution}/>
            }
            {
                workflowExecutionHistory &&
                tableContent
            }
        </div>
    )
}

export default WorkflowHistoryPage
